// imports
import { lazy, Suspense } from 'react';
import { styled } from '@mui/material/styles';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useSelector } from 'react-redux';
import { useRef, useCallback } from 'react';

// local imports
const DeckMap = lazy(() => import('./DeckMap/DeckMap'));
import type { DeckMapHandle } from './DeckMap/DeckMap';
import USGSWaveLoader from './USGSWaveLoader/USGSWaveLoader';
import InfoDialog from './InfoDialog/InfoDialog';
import PrimaryNavigation from './PrimaryNavigation/PrimaryNavigation';
import SidePanel from './SidePanel/SidePanel';
import USWDSBanner from './USWDSBanner/USWDSBanner';
import USGSHeader from './USGSHeader/USGSHeader';
import USGSFooter from './USGSFooter/USGSFooter';
import theme from './theme';
import ShareDialog from './ShareDialog/ShareDialog';
import DownloadButton from './DownloadButton/DownloadButton';
import MapDownloadDialog from './MapDownloadDialog/MapDownloadDialog';
import DataDownloadDialog from './DataDownloadDialog/DataDownloadDialog';
import BasemapInitializer from './BasemapInitializer/BasemapInitializer';
import MapViewInitializer from './MapViewInitializer/MapViewInitializer';
import AWDInitializer from './AWDInitializer/AWDInitializer';
import InfoDialogInitializer from './InfoDialogInitializer/InfoDialogInitializer';
import ViewDataBanner from './ViewDataBanner/ViewDataBanner';
import SkipLink from './SkipLink/SkipLink';
import SiteAlert from './SiteAlert/SiteAlert';
import ShareButton from './ShareButton/ShareButton';
import CustomMapControls from './DeckMap/CustomMapControls/CustomMapControls';
import { RootState } from '../store';

// Grid Layout Setup
const AppContainer = styled('div')(() => ({
  display: 'grid',
  gridTemplateRows: 'auto auto 1fr auto', // Banner, Header+Nav, Main area, Footer
  gridTemplateColumns: '100%',
  height: '100vh',
  width: '100vw',
}));

const HeaderContainer = styled('header')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  flexWrap: 'wrap',

  [theme.breakpoints.up(1024)]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingLeft: 0,
    paddingRight: 0,
  },

  [theme.breakpoints.down(1023)]: {
    paddingLeft: 0,
    paddingRight: 0,
    minHeight: '48px',
    '& > *:first-of-type': {
      flex: 1,
      minWidth: 0,
    },
  },
}));

const MainContent = styled('main')(() => ({
  position: 'relative', // allows SidePanel to be absolutely positioned
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 1,
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
}));

function App() {
  const sidePanelOpen = useSelector((state: RootState) => state.map.sidePanelOpen);
  const isFullExtent = useSelector((state: RootState) => state.ui.isFullExtent);
  const zoom = useSelector((state: RootState) => state.map.viewState.zoom);
  const deckMapRef = useRef<DeckMapHandle>(null);

  const handleZoomIn = useCallback(() => deckMapRef.current?.zoomIn(), []);
  const handleZoomOut = useCallback(() => deckMapRef.current?.zoomOut(), []);
  const handleFlyTo = useCallback((center: [number, number], zoom: number) => deckMapRef.current?.flyTo(center, zoom), []);
  const handleResetView = useCallback(() => deckMapRef.current?.resetView(), []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppContainer id="app-container" sx={isFullExtent ? { gridTemplateRows: '1fr' } : undefined}>
        <SkipLink />
        {!isFullExtent && <USWDSBanner />}

        {!isFullExtent && (
          <HeaderContainer id="header-container">
            <USGSHeader />
            <PrimaryNavigation />
            <SiteAlert />
          </HeaderContainer>
        )}

        <MainContent id="main-content">
          <h1 className="usa-sr-only">National Water Data Companion Interactive Map</h1>
          <SidePanel />
          <BasemapInitializer />
          <MapViewInitializer />
          <AWDInitializer />
          <InfoDialogInitializer />
          <Suspense fallback={<USGSWaveLoader />}>
            <ViewDataBanner left={sidePanelOpen ? '410px' : '10px'} />
            <DeckMap ref={deckMapRef} />
            {import.meta.env.DEV && <DownloadButton />}
            <ShareButton />
            <CustomMapControls
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onFlyTo={handleFlyTo}
              onResetView={handleResetView}
              zoom={zoom}
            />
          </Suspense>
        </MainContent>

        {!isFullExtent && <USGSFooter />}
        <MapDownloadDialog />
        <DataDownloadDialog />
        <ShareDialog />
        <InfoDialog />
      </AppContainer>
    </ThemeProvider>
  );
}

export default App;
